import {
  Box,
  Button,
  InlineStack,
  TableTd,
  TableTr,
  Text,
  TextField,
  TextLabel,
} from '@customerio/pluma-components/react';
import { IMMUTABLE_OBJECT_ATTRIBUTES, PERMANENT_OBJECT_ATTRIBUTES } from '@/services/db/object/entity';
import { ObjectAttributeTypeLabel } from './object-attribute-type-label';
import type { AttributeRow, AttributeRowErrors } from './use-object-attributes';

type ObjectAttributeRowProps = {
  row: AttributeRow;
  errors: AttributeRowErrors;
  isNew: boolean;
  isSaving: boolean;
  /** Errors only surface once the editor is dirty. */
  showErrors: boolean;
  isMultiIdentifier: boolean;
  onKeyChange: (row: AttributeRow, key: string) => void;
  onValueChange: (row: AttributeRow, value: string) => void;
  onRemove: (row: AttributeRow) => void;
};

export function ObjectAttributeRow(props: ObjectAttributeRowProps) {
  const { row, isNew, isSaving, showErrors } = props;
  const isCreated = row.originalKey == null;
  const isKeyChanged = row.key !== (row.originalKey ?? '');
  const isImmutable = IMMUTABLE_OBJECT_ATTRIBUTES.includes(row.key);
  const isValueDisabled = isImmutable && !isNew && !isKeyChanged;
  const isPermanentAndNotChanged = isImmutable || (PERMANENT_OBJECT_ATTRIBUTES.includes(row.key) && !isKeyChanged);
  const keyError = showErrors ? props.errors.key : undefined;
  const valueError = showErrors ? props.errors.value : undefined;
  const valueFieldId = `attribute-value-${row.id}`;

  return (
    <TableTr data-test-attribute-editor>
      <TableTd
        data-test-attribute-key={row.key}
        overflowWrap="words"
        style={{ width: '256px' }}
        verticalAlign={isCreated || !isPermanentAndNotChanged ? 'top' : undefined}
      >
        {isCreated || !isPermanentAndNotChanged ? (
          <TextField
            data-test-editor-key-input={row.key}
            ariaLabel="Attribute name"
            value={row.key}
            isDisabled={isSaving}
            placeholder="Attribute name"
            error={keyError}
            isInvalid={keyError != null}
            onInput={(event) => {
              if (event.target instanceof HTMLInputElement) props.onKeyChange(row, event.target.value);
            }}
          />
        ) : (
          <TextLabel
            htmlFor={valueFieldId}
            display="flex"
            alignItems="center"
            gap="050"
            weight="regular"
            size="xxs"
            textTransform="none"
          >
            <Text as="span" data-test-editor-key-label>
              {row.key}
            </Text>
            <ObjectAttributeTypeLabel
              attribute={row.key}
              isMultiIdentifier={props.isMultiIdentifier}
              ignoreIdentifiers
            />
          </TextLabel>
        )}
      </TableTd>
      <TableTd verticalAlign={!isValueDisabled ? 'top' : undefined}>
        {!isValueDisabled ? (
          <InlineStack alignBlock="center" gap="050">
            <Box flex={1}>
              <TextField
                data-test-editor-value-input={row.key}
                id={valueFieldId}
                className="fs-mask"
                ariaLabel="Attribute value"
                value={row.value}
                isDisabled={isSaving}
                placeholder="Value"
                error={valueError}
                isInvalid={valueError != null}
                onInput={(event) => {
                  if (event.target instanceof HTMLInputElement) props.onValueChange(row, event.target.value);
                }}
              />
            </Box>
            {!isPermanentAndNotChanged ? (
              <Button
                icon="delete-trash"
                isDisabled={isSaving}
                isIconOnly
                onClick={() => props.onRemove(row)}
                size="sm"
                variant="tertiary"
                aria-label={`Remove ${row.key} attribute`}
                data-test-editor-remove={row.key}
              />
            ) : null}
          </InlineStack>
        ) : (
          <Text as="div" className="fs-mask">
            {row.value}
          </Text>
        )}
      </TableTd>
    </TableTr>
  );
}
